<template>
  <SmDialog
    v-model:visibility="visibility"
    :title="i18n.t('export')"
    size="small">
    <template #default>
      <div class="space-y-6">
        <div class="flex items-center justify-between gap-4">
          <p>
            {{ i18n.t('accountingExport.includeRows') }}
          </p>
          <SmSwitch v-model="includeDetailRows" />
        </div>

        <div class="mb-4 flex items-center justify-between gap-4">
          <label> Format </label>
          <el-radio-group v-model="exportFormat">
            <el-radio-button :label="OutputFormat.CSV" />
            <el-radio-button :label="OutputFormat.JSON" />
            <el-radio-button :label="OutputFormat.XLSX" />
          </el-radio-group>
        </div>
      </div>
    </template>

    <template #footer>
      <SmButton v-if="!exportLoading" outline @click="visibility = false">
        {{ i18n.t('general.cancel') }}
      </SmButton>

      <SmButton :loading="exportLoading" @click="exportAccountingData">
        {{ i18n.t('export') }}
      </SmButton>
    </template>
  </SmDialog>
</template>

<script setup lang="ts">
  import dayjs from 'dayjs'
  import { useI18n } from 'vue-i18n'
  import { ref, watch, computed } from 'vue'

  import {
    OutputFormat,
    PreferenceLanguages,
    CurrencyCode,
    ApiError,
  } from '@/client'
  import SmDialog from '@/components/sm/SmDialog.vue'
  import SmSwitch from '@/components/sm/SmSwitch.vue'
  import SmButton from '@/components/sm/SmButton.vue'
  import {
    exportAccountingImportRows,
    exportAccountingImportStatistics,
  } from '@/utils/export'
  import { sendToast } from '@/components/sm/SmNotification'
  import { getApiErrorMessage } from '@/utils/errors'

  interface Props {
    accountingImportId: string
    targetCurrencyCode?: CurrencyCode | null
    filters?: {
      softwareNamesRegex?: string
      softwareNamesOptions?: string
      aiPredictionEq?: boolean
      predictionCertaintyGte?: number
      blacklistedEq?: boolean
    }
  }

  const props = withDefaults(defineProps<Props>(), {
    targetCurrencyCode: undefined,
    filters: undefined,
  })

  const i18n = useI18n()
  const visibility = defineModel('visibility', {
    type: Boolean,
    default: false,
  })

  const exportFormat = ref<OutputFormat>(OutputFormat.CSV)
  const includeDetailRows = ref(false)
  const exportLoading = ref(false)
  const safeTargetCurrencyCode = computed<CurrencyCode | undefined>(() => {
    return (props.targetCurrencyCode ?? undefined) as CurrencyCode | undefined
  })
  const filters = computed(() => props.filters ?? {})

  watch(visibility, function resetDialog(open) {
    if (!open) return
    exportFormat.value = OutputFormat.CSV
    includeDetailRows.value = false
  })

  async function exportAccountingData() {
    if (!props.accountingImportId) {
      return
    }
    exportLoading.value = true
    try {
      const baseName = `${dayjs().format('YYYY-MM-DD')}-accounting-import-${props.accountingImportId}`
      const language = i18n.locale.value as PreferenceLanguages

      const statisticsResponse = await exportAccountingImportStatistics({
        accountingImportId: props.accountingImportId,
        requestBody: {
          output_format: exportFormat.value,
          language: language,
          column_keys_human_readable: true,
          cost_config: {
            target_currency_code: safeTargetCurrencyCode.value,
          },
        },
        queryParams: {
          accountingImportId: props.accountingImportId,
          ...filters.value,
        },
      })

      await saveExportResponse(
        statisticsResponse,
        `${baseName}-statistics.${exportFormat.value.toLowerCase()}`
      )
      sendToast(
        i18n.t('accountingExport.statisticsSuccess'),
        undefined,
        'success'
      )

      if (includeDetailRows.value) {
        const rowsResponse = await exportAccountingImportRows({
          requestBody: {
            output_format: exportFormat.value,
            language: language,
            column_keys_human_readable: true,
            cost_config: {
              target_currency_code: safeTargetCurrencyCode.value,
            },
          },
          queryParams: {
            accountingImportId: props.accountingImportId,
            ...filters.value,
          },
        })

        await saveExportResponse(
          rowsResponse,
          `${baseName}-rows.${exportFormat.value.toLowerCase()}`
        )
        sendToast(i18n.t('accountingExport.rowsSuccess'), undefined, 'success')
      }

      visibility.value = false
    } catch (error) {
      console.error(error)
      const message =
        error instanceof ApiError
          ? getApiErrorMessage(error, i18n as ReturnType<typeof useI18n>)
          : i18n.t('error.unknown.unknown_error.message')
      sendToast(message, undefined, 'error')
    } finally {
      exportLoading.value = false
    }
  }

  async function saveExportResponse(response: Response, filename: string) {
    if (!response || response.status !== 200) {
      throw new Error('Export failed')
    }

    let dataToWrite: BlobPart
    let mimeType = 'text/plain'

    if (exportFormat.value === OutputFormat.JSON) {
      const jsonValue = await response.json()
      dataToWrite = JSON.stringify(jsonValue, null, 2)
      mimeType = 'application/json'
    } else if (exportFormat.value === OutputFormat.XLSX) {
      dataToWrite = await response.blob()
      mimeType =
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    } else {
      dataToWrite = await response.text()
      mimeType = 'text/csv'
    }

    const blob = new Blob([dataToWrite], { type: mimeType })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', filename)
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
  }
</script>
